/**
 * nen5740-uitleg.js — de inhoud van de popup 'Bekijk uitleg' bij stap 2 (Strategie) van het
 * vooronderzoek: per onderzoeksstrategie wanneer hij van toepassing is, en de tabel uit de norm
 * met de aantallen boringen en (meng)monsters.
 *
 * Bron: NEN 5740:2023+C1:2024, hoofdstuk 4 (keuze van de strategie) en § 5.1 en 5.3 t/m 5.6
 * (tabel 2, 3 en 6 t/m 12). De uitleg is in eigen woorden; de tabellen geven de aantallen van de
 * norm weer, de voetnoten kort samengevat. Het rekenwerk zelf staat in
 * packages/models/src/sampling/nen5740.ts -- dit bestand is alleen om te lezen.
 *
 * Een tabel: `kop` is een lijst kopregels, elke cel { t, cs?, rs? } (colspan/rowspan); `rijen`
 * zijn de gewone regels; `noten` de voetnoten onder de tabel, met hun letter.
 */

/** Grondwater, peilbuis, grond: dezelfde kop komt bij meerdere tabellen terug. */
const c = (t, cs, rs) => ({ t, cs, rs });

export const NEN5740_BRON = 'NEN 5740:2023+C1:2024';

export const NEN5740_INTRO = {
  titel: 'Introductie',
  alineas: [
    'NEN 5740 beschrijft hoe je een verkennend bodemonderzoek uitvoert: hoeveel boringen en peilbuizen je zet, hoe diep, en hoeveel (meng)monsters je laat analyseren. Welke aantallen gelden, hangt af van de onderzoeksstrategie die je per (deel)locatie kiest.',
    'Die keuze volgt uit het vooronderzoek (NEN 5725). Eerst bepaal je of een deellocatie onverdacht of verdacht is. Bij een verdachte deellocatie kijk je naar het verwachte verontreinigingsbeeld: is er een duidelijke kern (een plaatselijke bron), of is de belasting diffuus over de hele deellocatie verspreid? En als hij diffuus is: is de verontreiniging gelijkmatig (homogeen) of juist wisselend (heterogeen) verdeeld?',
    'Elke strategie bestaat in een vlakvormige variant (NL, niet-lijnvormig: de aantallen hangen af van de oppervlakte) en, waar de norm dat kent, een lijnvormige variant (L: een tracé, de aantallen hangen af van de lengte).',
    'GeoVoice rekent per deellocatie het minimum uit dat de norm voorschrijft. Wat de norm aan de uitvoerder overlaat, zoals het combineren van strategieën, extra boringen bij zintuiglijke waarnemingen of de plaatsing ten opzichte van de stromingsrichting van het grondwater, doe je zelf.',
  ],
  overzicht: {
    titel: 'Welke strategie past bij welk beeld',
    kop: [[c('Strategie'), c('Verontreinigingsbeeld'), c('Voorbeelden uit de praktijk')]],
    rijen: [
      ['ONV', 'Geen verdenking', 'Locatie zonder bodembedreigende activiteiten in het verleden'],
      ['VEP', 'Duidelijke kern, geen ondergrondse tank', 'Morsingen, lekkages, brandplaatsen'],
      ['VEP-OO', 'Duidelijke kern bij een ondergrondse tank', 'Ondergrondse brandstoftank, olie-waterscheider'],
      ['VED-HO', 'Geen duidelijke kern, homogeen verdeeld', 'Ophoging met slib, atmosferische depositie'],
      ['VED-HE', 'Geen duidelijke kern, heterogeen verdeeld', '(Stedelijke) ophogingen, stortingen'],
    ],
    bron: 'naar NEN 5740 § 4.1 en tabel 1',
  },
};

/**
 * Per strategie: wanneer hij van toepassing is (`uitleg`), voorbeelden uit de praktijk, en de
 * tabel(len). `code` is gelijk aan die in nen5740.ts, zodat de
 * popup kan openen op de strategie die bij de deellocatie gekozen is.
 */
export const NEN5740_UITLEG = [
  {
    code: 'ONV-NL',
    naam: 'Onverdacht, niet-lijnvormig',
    bron: '§ 5.1, tabel 2',
    uitleg: 'Voor een deellocatie waar het vooronderzoek geen aanwijzingen geeft voor bodembelasting. Het onderzoek moet laten zien dat bodem en grondwater relatief onbelast zijn. De boringen en peilbuizen worden gelijkmatig over het vlak verdeeld; een enkele peilbuis komt in het midden, of anders zo veel mogelijk aan de stroomafwaartse kant. Je boort de bovengrond tot 0,5 m, een deel van de boringen tot 0,5 m onder de grondwaterspiegel (minimaal 1 m, hooguit 2 m), en de peilbuizen tot in het freatisch grondwater. Ten hoogste tien grondmonsters gaan in één mengmonster, en verschillende bodemsoorten meng je niet.',
    voorbeelden: [
      'Een bouwkavel zonder bekend bodembedreigend gebruik in het verleden',
      'Weiland of akker waar het vooronderzoek geen verdachte activiteiten oplevert',
      'Een tuin of erf bij een woning zonder olietank of opslag',
    ],
    tabellen: [{
      titel: 'Tabel 2 — Onverdachte niet-lijnvormige locatie (ONV-NL)',
      kop: [
        [c('Oppervlakte locatie A (ha)', 1, 2), c('Aantal boringen', 3), c('Aantal te analyseren (meng)monsters', 3)],
        [c('Boring tot 0,5 m'), c('Én boring tot grondwater ᵃ'), c('Én boring met peilbuis ᵇ'), c('Bovengrond'), c('Ondergrond'), c('Grondwater')],
      ],
      rijen: [
        ['A ≤ 0,01', '2', '–', '1', '1', '1', '1'],
        ['0,01 < A ≤ 0,05', '2', '1', '1', '1', '1', '1'],
        ['0,05 < A ≤ 0,10', '4', '1', '1', '1', '1', '1'],
        ['0,10 < A ≤ 0,15', '6', '1', '1', '1', '1', '1'],
        ['0,15 < A ≤ 0,20', '8', '2', '1', '2', '1', '1'],
        ['0,20 < A ≤ 0,30', '9', '2', '1', '2', '1', '1'],
        ['0,30 < A ≤ 0,40', '10', '2', '1', '2', '1', '1'],
        ['0,40 < A ≤ 0,50', '11', '3', '1', '2', '1', '1'],
        ['0,50 < A ≤ 0,70', '12', '3', '1', '2', '2', '1'],
        ['0,70 < A ≤ 0,90', '13', '4', '2', '3', '2', '2'],
        ['0,90 < A ≤ 1,0', '14', '4', '2', '3', '2', '2'],
        ['A > 1,0', '7 + 7p ᶜ', '2 + 2p', '1 + 1p', '2 + 1p', '1 + 1p', '1 + 1p'],
      ],
      noten: [
        ['a', 'Grondwater ondieper dan 1,0 m –mv: boordiepte 1,0 m. Dieper dan 2,0 m –mv: boordiepte 2,0 m.'],
        ['b', 'Grondwater dieper dan 5,0 m –mv: peilbuis mag vervallen, wel boren tot 2 m. Grondwaterstand onbekend: boordiepte 5 m.'],
        ['c', 'p = oppervlakte in ha (p > 1). Naar boven afronden; het totaal aantal boringen is steeds 10 + 10p.'],
      ],
    }],
  },
  {
    code: 'ONV-L',
    naam: 'Onverdacht, lijnvormig',
    bron: '§ 5.1, tabel 3',
    uitleg: 'Dezelfde situatie als ONV-NL, maar voor een tracé. De aantallen hangen af van de lengte in plaats van de oppervlakte, en de boringen gaan tot 0,25 m onder de ontgravingsdiepte. Grondwater onderzoek je alleen als dat hoger staat dan 0,25 m onder de ontgravingsdiepte. Ligt er langs het tracé een plek die wél verdacht is, dan onderzoek je die daarnaast met de strategie VEP.',
    voorbeelden: [
      'Een kabel- of leidingtracé door onverdacht gebied',
      'Vervanging van riolering in een straat zonder verdachte activiteiten',
      'Aanleg van een nieuwe weg of fietspad',
    ],
    tabellen: [{
      titel: 'Tabel 3 — Onverdachte lijnvormige locatie (ONV-L)',
      kop: [
        [c('Lengte tracé L (m)', 1, 2), c('Aantal boringen', 2), c('Aantal te analyseren (meng)monsters', 3), c('p ᵇ', 1, 2)],
        [c('Boring tot 0,25 m onder ontgravingsdiepte'), c('Én boring met peilbuis ᵃ'), c('Bovengrond'), c('Ondergrond'), c('Grondwater')],
      ],
      rijen: [
        ['L ≤ 1 000', '1p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', 'L / 50'],
        ['1 000 < L ≤ 10 000', '10 + p', '1 + 0,1p', '1 + 0,1p', '1 + 0,1p', '1 + 0,1p', 'L / 100'],
      ],
      noten: [
        ['a', 'Alleen grondwateronderzoek als het grondwater hoger staat dan 0,25 m onder de maximale ontgravingsdiepte; anders worden de peilbuizen boringen tot 0,25 m onder de ontgravingsdiepte.'],
        ['b', 'Naar boven afronden op gehele getallen.'],
      ],
    }],
  },
  {
    code: 'VEP',
    naam: 'Verdacht, plaatselijke kern',
    bron: '§ 5.3, tabel 6',
    uitleg: 'Voor een deellocatie met een duidelijke, plaatselijke bron van verontreiniging zonder ondergrondse tank. Het onderzoek moet aantonen of de verwachte kern er echt zit. De boringen en de peilbuis komen in de kern zelf, of zo dicht mogelijk ernaast (bij voorkeur benedenstrooms), en gaan tot 0,5 m onder de kern (hooguit 5 m). De aantallen gelden per kern. Per kern worden hooguit vijf monsters gemengd; zintuiglijk verontreinigde monsters meng je niet met schone. Bij een kern groter dan 0,1 ha met een gelijkmatig verdeelde stof gebruik je VED-HO.',
    voorbeelden: [
      'Een morsing bij een vul- of aftappunt, of in een werkplaats',
      'Een lekkage bij de opslag van chemicaliën of olie (bovengronds)',
      'Een brandplaats of stookplaats',
      'Een afleverinstallatie (pompeiland) van een tankstation',
    ],
    tabellen: [{
      titel: 'Tabel 6 — Verdachte locatie met een duidelijke kern (VEP), per kern',
      kop: [
        [c('Oppervlakte kern A (ha)', 1, 2), c('Aantal boringen', 2), c('Aantal te analyseren (meng)monsters', 2)],
        [c('Boring tot 0,5 m onder kern ᵃ'), c('Én boring met peilbuis ᵈ'), c('Grond (kern)'), c('Grondwater')],
      ],
      rijen: [
        ['A ≤ 0,01 ᵇ', '2', '1', '1', '1'],
        ['0,01 < A ≤ 0,05', '3', '1', '1', '1'],
        ['0,05 < A ≤ 0,08', '4', '1', '1', '1'],
        ['0,08 < A ≤ 0,10 ᶜ', '5', '1', '1', '1'],
      ],
      noten: [
        ['a', 'Minimaal tot 0,5 m onder de kern; 5,0 m als de onderzijde van de verontreiniging dieper ligt dan 5 m.'],
        ['b', 'Bij een puntbron (< 0,001 ha) met een duidelijk waarneembare kern volstaat één boring, of bij vluchtige mobiele stoffen één peilbuis.'],
        ['c', 'De norm gaat uit van een kern van hooguit 0,1 ha. Bij een grotere kern met een homogeen verdeelde stof: zie VED-HO (tabel 9 of 10).'],
        ['d', 'Grondwater dieper dan 5,0 m –mv: de peilbuis wordt een boring tot 0,5 m onder de kern, met een minimum van 2 m –mv.'],
      ],
    }],
  },
  {
    code: 'VEP-OO',
    naam: 'Verdacht, ondergrondse opslagtank',
    bron: '§ 5.4, tabel 7 en 8',
    uitleg: 'Voor een locatie met één of meer ondergrondse opslagtanks. Onderzocht worden de tank zelf, het vul- en ontluchtingspunt en de leidingen naar de tank; de afleverinstallatie hoort er niet bij (die valt onder VEP). Boringen bij de tank gaan tot 0,5 m onder de onderzijde van de tank, bij het vul- en ontluchtingspunt tot 1,0 m, en langs de leidingen tot 0,5 m eronder. Eén tank: tabel 7; twee of meer tanks dicht bij elkaar: tabel 8 (liggen ze meer dan 10 m uit elkaar, dan zijn het aparte installaties). Je onderzoekt op alles wat in de tank opgeslagen is (geweest).',
    voorbeelden: [
      'Een ondergrondse huisbrandolietank bij een woning',
      'Een ondergrondse brandstoftank bij een garage of boerderij',
      'Een olie-waterscheider, bijvoorbeeld bij een wasplaats',
      'Een tank die al verwijderd of gesaneerd is',
    ],
    tabellen: [
      {
        titel: 'Tabel 7 — Eén ondergrondse tank',
        kop: [
          [c('Inhoud tank V (m³)', 1, 3), c('Tank', 4), c('Vulpunt ᵃ', 2), c('Ontluchtingspunt ᵃ', 2)],
          [c('Aantal boringen', 2), c('Te onderzoeken', 2), c('Boringen tot 1,0 m', 1, 2), c('Grond-monsters', 1, 2), c('Boringen tot 1,0 m', 1, 2), c('Grond-monsters', 1, 2)],
          [c('Tot 0,5 m onder tank'), c('Én met peilbuis ᵇ'), c('Grond'), c('Grondwater')],
        ],
        rijen: [
          ['V ≤ 3 ᶜ', '1', '1', '1', '1', '1', '1', '1', '1'],
          ['3 < V ≤ 5', '1', '1', '1', '1', '1', '1', '1', '1'],
          ['5 < V ≤ 10', '2', '1', '2', '1', '1', '1', '1', '1'],
          ['10 < V ≤ 20', '2', '1', '2', '1', '1', '1', '1', '1'],
          ['20 < V ≤ 50', '3', '1', '2', '1', '1', '1', '1', '1'],
          ['V > 50', '3', '1', '2', '1', '1', '1', '1', '1'],
        ],
        noten: [
          ['a', 'Vervalt als het vul- of ontluchtingspunt binnen 2 m van de tankwand ligt. Bij een vloeistofdichte voorziening komt er een peilbuis aan de rand daarvan.'],
          ['b', 'Grondwater dieper dan 5,0 m –mv: de peilbuis wordt een boring tot 5 m –mv.'],
          ['c', 'Bij een olie-waterscheider is de inhoud niet relevant: ga uit van de kleinste klasse.'],
        ],
      },
      {
        titel: 'Tabel 8 — Twee of meer ondergrondse tanks dicht bij elkaar',
        kop: [
          [c('Inhoud tanks V (m³)', 1, 3), c('Tank', 4), c('Vulpunt ᵃ', 2), c('Ontluchtingspunt ᵃ', 2)],
          [c('Aantal boringen', 2), c('Te onderzoeken', 2), c('Boringen tot 1,0 m', 1, 2), c('Grond-monsters', 1, 2), c('Boringen tot 1,0 m', 1, 2), c('Grond-monsters', 1, 2)],
          [c('Tot 0,5 m onder tank'), c('Én met peilbuis ᵇ'), c('Grond'), c('Grondwater')],
        ],
        rijen: [
          ['V ≤ 10', '2', '1', '1', '1', '1', '1', '1', '1'],
          ['10 < V ≤ 25', '2', '1', '1', '1', '1', '1', '1', '1'],
          ['25 < V ≤ 50', '3', '1', '2', '1', '1', '1', '1', '1'],
          ['50 < V ≤ 100', '3', '1', '3', '1', '1', '1', '1', '1'],
          ['V > 100', '3', '2', '3', '2', '1', '1', '1', '1'],
        ],
        noten: [
          ['a', 'Als bij tabel 7. Liggen de vul- of ontluchtingspunten van één cluster niet bij elkaar, dan gelden de aantallen per punt.'],
          ['b', 'Grondwater dieper dan 5,0 m –mv: de peilbuis wordt een boring tot 5 m –mv.'],
        ],
      },
      {
        titel: 'Leidingen (bij tabel 7 en 8)',
        kop: [[c('Lengte'), c('Boringen tot 0,5 m onder de leiding'), c('Te onderzoeken grondmonsters')]],
        rijen: [['Per 10 m leiding', '2', '1']],
        noten: [
          ['', 'Alleen als er meer dan 5 m leiding ligt tussen vulpunt, ontluchting of afleverinstallatie en de tank. Vuistregel: één boring per 5 m, en minimaal één grondmonster per leiding.'],
        ],
      },
    ],
  },
  {
    code: 'VED-HO-NL',
    naam: 'Verdacht, diffuus homogeen, vlak',
    bron: '§ 5.5, tabel 9',
    uitleg: 'Voor een deellocatie die over de hele oppervlakte belast kan zijn, zonder duidelijke kern, waarbij de verontreiniging gelijkmatig verdeeld is over de verdachte laag. Het onderzoek bepaalt de kwaliteit van de verdachte laag en het grondwater. De boringen worden gelijkmatig verdeeld en gaan tot 0,5 m onder de verdachte laag; je neemt monsters per 0,5 m. Hooguit vier grondmonsters van de verdachte laag gaan in één mengmonster, en verschillende bodemsoorten meng je niet.',
    voorbeelden: [
      'Een ophoging met baggerspecie of slib',
      'Atmosferische depositie, bijvoorbeeld rond (voormalige) industrie',
    ],
    tabellen: [{
      titel: 'Tabel 9 — Diffuus belaste niet-lijnvormige locatie, homogeen verdeeld (VED-HO-NL)',
      kop: [
        [c('Oppervlakte locatie A (ha)', 1, 2), c('Aantal boringen', 2), c('Aantal te analyseren (meng)monsters', 2)],
        [c('Boring tot 0,5 m onder kern'), c('Én boring met peilbuis ᵃ'), c('Grond (verdachte laag) ᵇ'), c('Grondwater')],
      ],
      rijen: [
        ['A < 1', '5', '2', '2', '2'],
        ['A ≥ 1', '5 + p ᶜ', '2 + 0,5p', '2 + 0,5p', '2 + 0,5p'],
      ],
      noten: [
        ['a', 'Grondwater dieper dan 5 m –mv: de peilbuizen worden boringen tot ten minste 5 m –mv.'],
        ['b', 'De aantallen gelden ongeacht welke bodemlaag verdacht is.'],
        ['c', 'p = oppervlakte in ha. Naar boven afronden; het totaal aantal boringen is steeds 7 + 1,5p.'],
      ],
    }],
  },
  {
    code: 'VED-HO-L',
    naam: 'Verdacht, diffuus homogeen, lijnvormig',
    bron: '§ 5.5, tabel 10',
    uitleg: 'Dezelfde situatie als VED-HO-NL (diffuus en gelijkmatig belast), maar voor een tracé. De aantallen hangen af van de lengte. Grondwater onderzoek je alleen als dat hoger staat dan 0,25 m onder de ontgravingsdiepte, behalve bij vluchtige stoffen die in de sleuf kunnen uitdampen. Een verdachte plek langs het tracé onderzoek je daarnaast met VEP.',
    voorbeelden: [
      'Een berm of oever langs een watergang waar slib of baggerspecie op is gezet',
      'Een tracé door een gebied met atmosferische depositie',
    ],
    tabellen: [{
      titel: 'Tabel 10 — Diffuus belaste lijnvormige locatie, homogeen verdeeld (VED-HO-L)',
      kop: [
        [c('Lengte tracé L (m)', 1, 2), c('Aantal boringen', 2), c('Aantal te analyseren (meng)monsters', 3), c('p ᵇ', 1, 2)],
        [c('Boring tot 0,5 m onder kern'), c('Én boring met peilbuis ᵃ'), c('Bovengrond'), c('Ondergrond'), c('Grondwater')],
      ],
      rijen: [
        ['L < 1 000', '1p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', 'L / 50'],
        ['1 000 – 10 000', '10 + p', '1 + 0,1p', '1 + 0,1p', '1 + 0,1p', '1 + 0,1p', 'L / 100'],
        ['L > 10 000', '60 + p', '1 + 0,2p', '1 + 0,2p', '1 + 0,2p', '1 + 0,2p', 'L / 200'],
      ],
      noten: [
        ['a', 'Alleen grondwateronderzoek als het grondwater hoger staat dan 0,25 m onder de maximale ontgravingsdiepte, tenzij vluchtige stoffen kunnen uitdampen in de sleuf. Anders worden de peilbuizen boringen tot 0,25 m onder de ontgravingsdiepte.'],
        ['b', 'Naar boven afronden op gehele getallen.'],
      ],
    }],
  },
  {
    code: 'VED-HE-NL',
    naam: 'Verdacht, diffuus heterogeen, vlak',
    bron: '§ 5.6, tabel 11',
    uitleg: 'Voor een deellocatie die over de hele oppervlakte belast kan zijn, zonder duidelijke kern, waarbij de verontreiniging wisselend verdeeld is: de concentratie verschilt van plek tot plek. Daarom zijn er meer boringen in de verdachte laag dan bij ONV-NL; de boringen tot 2 m en de peilbuizen zijn gelijk aan ONV-NL. De boringen gaan 0,5 m in de verdachte laag, een deel tot de onderzijde ervan (hooguit 2 m onder de bovenzijde). Hooguit vier grondmonsters gaan in één mengmonster. Bij een stedelijke ophooglaag analyseer je het volledige standaardpakket.',
    voorbeelden: [
      'Een (stedelijke) ophooglaag met puin, sintels of kolengruis',
      'Een voormalige storting of stortplaats',
      'Een gedempte sloot of watergang, gedempt met onbekend materiaal',
    ],
    tabellen: [{
      titel: 'Tabel 11 — Diffuus belaste niet-lijnvormige locatie, heterogeen verdeeld (VED-HE-NL)',
      kop: [
        [c('Oppervlakte locatie A (ha)', 1, 2), c('Aantal boringen', 3), c('Aantal te analyseren mengmonsters', 2)],
        [c('Boring tot 0,5 m in de verdachte laag'), c('Én boring tot onderzijde verdachte laag (max. 2 m)'), c('Én boring met peilbuis ᵃ ᵇ'), c('Grond (verdachte laag)'), c('Grondwater ᵇ')],
      ],
      rijen: [
        ['A ≤ 0,01', '2', '1', '1', '1', '1'],
        ['0,01 < A ≤ 0,05', '3', '1', '1', '2', '1'],
        ['0,05 < A ≤ 0,10', '5', '1', '1', '3', '1'],
        ['0,10 < A ≤ 0,15', '7', '1', '1', '3', '1'],
        ['0,15 < A ≤ 0,20', '10', '2', '1', '3', '1'],
        ['0,20 < A ≤ 0,30', '11', '2', '1', '3', '1'],
        ['0,30 < A ≤ 0,40', '12', '2', '1', '3', '1'],
        ['0,40 < A ≤ 0,50', '14', '3', '1', '3', '1'],
        ['0,50 < A ≤ 0,70', '15', '3', '1', '3', '1'],
        ['0,70 < A ≤ 0,90', '17', '4', '2', '4', '2'],
        ['0,90 < A ≤ 1,0', '18', '4', '2', '4', '2'],
        ['A > 1,0', '9 + 9p ᶜ', '2 + 2p', '1 + 1p', '2 + 2p', '1 + 1p'],
      ],
      noten: [
        ['a', 'Tot 0,5 m onder de verdachte laag. Grondwater dieper dan 5 m –mv: de peilbuizen worden boringen tot ten minste 5 m –mv.'],
        ['b', 'Wordt de verontreiniging vooral in het grondwater verwacht, dan gaat het aantal peilbuizen naar 4p.'],
        ['c', 'p = oppervlakte in ha (p > 1). Naar boven afronden; het totaal aantal boringen is steeds 12 + 12p.'],
      ],
    }],
  },
  {
    code: 'VED-HE-L',
    naam: 'Verdacht, diffuus heterogeen, lijnvormig',
    bron: '§ 5.6, tabel 12',
    uitleg: 'Dezelfde situatie als VED-HE-NL (diffuus en wisselend belast), maar voor een tracé. De aantallen hangen af van de lengte en de boringen gaan tot 0,25 m onder de ontgravingsdiepte. Grondwater onderzoek je alleen als dat hoger staat dan 0,25 m onder de ontgravingsdiepte, behalve bij vluchtige stoffen die in de sleuf kunnen uitdampen. Een verdachte plek langs het tracé onderzoek je daarnaast met VEP.',
    voorbeelden: [
      'Een tracé door een stedelijke ophooglaag',
      'Een weg of spoorbaan met een ophoging of fundering van puin, slakken of sintels',
    ],
    tabellen: [{
      titel: 'Tabel 12 — Diffuus belaste lijnvormige locatie, heterogeen verdeeld (VED-HE-L)',
      kop: [
        [c('Lengte tracé L (m)', 1, 2), c('Aantal boringen', 2), c('Aantal te analyseren (meng)monsters', 3), c('p ᵇ', 1, 2)],
        [c('Boring tot 0,25 m onder ontgravingsdiepte'), c('Én boring met peilbuis ᵃ'), c('Bovengrond'), c('Ondergrond'), c('Grondwater')],
      ],
      rijen: [
        ['Alle lengtes', '1p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', '1 + 0,05p', 'L / 50'],
      ],
      noten: [
        ['a', 'Alleen grondwateronderzoek als het grondwater hoger staat dan 0,25 m onder de maximale ontgravingsdiepte, tenzij vluchtige stoffen kunnen uitdampen in de sleuf. Anders worden de peilbuizen boringen tot 0,25 m onder de ontgravingsdiepte.'],
        ['b', 'Afronden op gehele getallen.'],
      ],
    }],
  },
];
